import React, { useState, useEffect, useCallback, useRef, type ChangeEvent } from 'react';
import { analyzeVariant } from './services/geminiService';
import { AnalysisState, ClinicalContext, EMPTY_CLINICAL_CONTEXT, VariantData } from './types';
import VariantReport from './components/VariantReport';
import { VariantChat } from './components/VariantChat';
import ClinicalContextInput from './components/ClinicalContextInput';
import ClinicalFitCard from './components/ClinicalFitCard';
import ClinicalActionCard from './components/ClinicalActionCard';
import NearbyVariantsCard from './components/NearbyVariantsCard';
import ReclassificationBadge from './components/ReclassificationBadge';
import { getFirebaseRedirectResult, signInWithEmailPassword, signInWithGoogle, signOutFromFirebase, signUpWithEmailPassword, subscribeToFirebaseAuth } from './services/firebaseAuth';
import { AUTH_SCREEN_STORAGE_KEY, normalizeAuthErrorMessage, shouldKeepAuthScreenVisible } from './approval-utils';
import type { User } from 'firebase/auth';
import { exportVariantReportAsPdf, variantPdfFilename } from './services/exportPdf';

type BatchAnalysisResult = {
  query: string;
  status: 'success' | 'error';
  data: VariantData | null;
  error: string | null;
};

const linkedTextParts = (text: string) => text.split(/(https:\/\/pubmed\.ncbi\.nlm\.nih\.gov\/\d+\/?)/g);

const LinkedSummaryText: React.FC<{ text: string; className?: string }> = ({ text, className }) => (
  <p className={className}>
    {linkedTextParts(text).map((part, index) => (
      part.startsWith('https://pubmed.ncbi.nlm.nih.gov/') ? (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-emerald-700 underline decoration-emerald-300 underline-offset-2 hover:text-emerald-900"
        >
          {part}
        </a>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    ))}
  </p>
);

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

const getPathogenicityScore = (data: VariantData): number | null => {
  if (typeof data.predictivePathogenicityScore === 'number' && Number.isFinite(data.predictivePathogenicityScore)) {
    return clamp01(data.predictivePathogenicityScore);
  }

  const scores = [data.alphaMissenseScore, data.revelScore]
    .filter((score): score is number => typeof score === 'number' && Number.isFinite(score) && score >= 0);

  if (scores.length === 0) return null;
  return clamp01(scores.reduce((sum, score) => sum + score, 0) / scores.length);
};

const PathogenicityBar: React.FC<{ data: VariantData }> = ({ data }) => {
  const score = getPathogenicityScore(data);

  if (score === null) return null;

  const percent = Math.round(score * 100);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div
        className="h-1.5"
        style={{
          background: 'linear-gradient(90deg, #2563eb 0%, #22c55e 35%, #facc15 55%, #f97316 75%, #dc2626 100%)',
        }}
      />
      <div className="p-5">
      <div className="mb-3 flex items-center justify-between gap-4">
        <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
          Pathogenicity
        </h3>
        <span className="rounded-full bg-slate-950 px-3 py-1 text-sm font-bold text-white shadow-sm">
          {score.toFixed(3)}
        </span>
      </div>
      <div
        className="relative h-6 rounded-full shadow-inner ring-1 ring-slate-200"
        style={{
          background: 'linear-gradient(90deg, #1d4ed8 0%, #06b6d4 22%, #22c55e 40%, #fde047 58%, #f97316 76%, #b91c1c 100%)',
        }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-l-full bg-white/20"
          style={{ width: `${percent}%` }}
        />
        <div
          className="absolute top-1/2 h-10 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-slate-950 shadow-lg"
          style={{ left: `${percent}%` }}
        />
      </div>
      <div className="mt-2 flex justify-between text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
        <span>Low</span>
        <span>High</span>
      </div>
      </div>
    </div>
  );
};

const ReportedClinicalFindingsCard: React.FC<{ data: VariantData }> = ({ data }) => {
  const reported = data.reportedClinicalFindings;

  if (!reported) return null;

  return (
    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 shadow-sm">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-emerald-950">
            <span>📋</span>
            Literature Clinical Findings
          </h3>
          <LinkedSummaryText
            text={reported.summary}
            className="mt-2 text-sm leading-relaxed text-emerald-950"
          />
        </div>
        <div className="flex shrink-0 flex-wrap gap-2 text-xs font-semibold">
          <span className="rounded-full border border-emerald-200 bg-white px-3 py-1 text-emerald-800">
            {reported.exactVariantEvidenceCount} exact variant literature
          </span>
          {reported.nearbyVariantEvidenceCount > 0 && (
            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-blue-800">
              {reported.nearbyVariantEvidenceCount} nearby literature
            </span>
          )}
        </div>
      </div>

      {reported.findings.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-emerald-100 bg-white">
          {reported.findings.slice(0, 6).map((finding, index) => (
            <div
              key={`${finding.source}-${finding.pmid || finding.accession || index}`}
              className="border-t border-emerald-50 px-4 py-3 first:border-t-0"
            >
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full bg-emerald-100 px-2 py-1 font-semibold text-emerald-800">
                  {finding.source}
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">
                  {finding.matchConfidence.replace(/_/g, ' ')}
                </span>
                {finding.condition && (
                  <span className="rounded-full bg-blue-50 px-2 py-1 text-blue-700">
                    {finding.condition}
                  </span>
                )}
                {finding.inheritancePattern && (
                  <span className="rounded-full bg-purple-50 px-2 py-1 text-purple-700">
                    {finding.inheritancePattern}
                  </span>
                )}
              </div>
              {finding.variantProximity && (
                <p className="mb-1 text-xs font-medium text-slate-500">{finding.variantProximity}</p>
              )}
              {finding.url && (
                <a
                  href={finding.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex text-sm font-medium text-emerald-700 hover:text-emerald-900"
                >
                  {finding.pmid ? `PMID:${finding.pmid}` : finding.accession || 'Source'}
                </a>
              )}
            </div>
          ))}
          {reported.findings.length > 6 && (
            <div className="border-t border-emerald-50 px-4 py-2 text-xs font-medium text-slate-500">
              +{reported.findings.length - 6} additional literature rows in the detailed Clinical tab
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const parseVariantQueries = (rawQuery: string) => rawQuery
  .split(/[\n;]+/)
  .map(item => item.trim())
  .filter(Boolean);

async function runWithConcurrency<T, R>(items: T[], limit: number, worker: (item: T, index: number) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  async function runNext() {
    const currentIndex = nextIndex;
    nextIndex += 1;
    if (currentIndex >= items.length) return;
    results[currentIndex] = await worker(items[currentIndex], currentIndex);
    await runNext();
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runNext));
  return results;
}

const AlphaVarExplorer: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isCheckingApproval, setIsCheckingApproval] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<'sign-in' | 'sign-up'>('sign-in');
  const [showAuthScreen, setShowAuthScreen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [query, setQuery] = useState('');
  const [clinicalContext, setClinicalContext] = useState<ClinicalContext>({ ...EMPTY_CLINICAL_CONTEXT });
  const [state, setState] = useState<AnalysisState>({
    status: 'idle',
    data: null,
    error: null,
  });
  const [batchResults, setBatchResults] = useState<BatchAnalysisResult[]>([]);
  const [activeBatchTab, setActiveBatchTab] = useState<'summary' | number>('summary');
  const [showAbout, setShowAbout] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const queryInputRef = useRef<HTMLTextAreaElement>(null);

  const resizeQueryInput = useCallback(() => {
    const input = queryInputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    const maxHeight = 240;
    input.style.height = `${Math.min(input.scrollHeight, maxHeight)}px`;
    input.style.overflowY = input.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  const handleQueryChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setQuery(event.target.value);
    window.requestAnimationFrame(resizeQueryInput);
  };

  useEffect(() => {
    resizeQueryInput();
  }, [query, resizeQueryInput]);

  const checkApprovalStatus = useCallback(async (activeUser: User | null) => {
    if (!activeUser) {
      setIsApproved(null);
      setApprovalError(null);
      setIsCheckingApproval(false);
      return;
    }

    setIsCheckingApproval(true);
    setApprovalError(null);

    try {
      const token = await activeUser.getIdToken(true);
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const rawBody = await response.text();
      let data: { success?: boolean; error?: string; approved?: boolean; source?: string } | null = null;

      try {
        data = rawBody ? JSON.parse(rawBody) : null;
      } catch {
        data = null;
      }

      if (!response.ok || !data?.success) {
        const errorMessage = data?.error
          || (rawBody && !rawBody.trim().startsWith('<') ? rawBody : null)
          || 'Failed to verify approval status';
        setApprovalError(errorMessage);
        setIsApproved(false);
        return;
      }

      if (data.source === 'firestore-denied') {
        setApprovalError('Access has not been approved for this account. Sign in again to submit a new request, or contact the administrator.');
      }

      setIsApproved(Boolean(data.approved));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to verify approval status';
      setApprovalError(message);
      setIsApproved(false);
    } finally {
      setIsCheckingApproval(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void getFirebaseRedirectResult()
      .then((result) => {
        if (cancelled) return;
        const storedIntent = typeof window !== 'undefined' && window.sessionStorage.getItem(AUTH_SCREEN_STORAGE_KEY) === '1';
        if (result?.user && typeof window !== 'undefined') {
          window.sessionStorage.removeItem(AUTH_SCREEN_STORAGE_KEY);
          setShowAuthScreen(false);
        } else if (storedIntent) {
          setShowAuthScreen(true);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setApprovalError(error instanceof Error ? error.message : 'Failed to complete sign in');
        setShowAuthScreen(true);
      });

    const unsubscribe = subscribeToFirebaseAuth((nextUser) => {
      const storedIntent = typeof window !== 'undefined' && window.sessionStorage.getItem(AUTH_SCREEN_STORAGE_KEY) === '1';
      setUser(nextUser);
      setIsLoadingAuth(false);
      setIsApproved(null);
      setApprovalError((current) => (nextUser ? null : current));
      setShowAuthScreen(shouldKeepAuthScreenVisible({
        hasUser: Boolean(nextUser),
        storedAuthIntent: storedIntent,
        redirectError: false,
      }));

      if (nextUser && typeof window !== 'undefined') {
        window.sessionStorage.removeItem(AUTH_SCREEN_STORAGE_KEY);
      }

      void checkApprovalStatus(nextUser);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [checkApprovalStatus]);

  useEffect(() => {
    if (!user || isApproved !== false) return;

    const intervalId = window.setInterval(() => {
      void checkApprovalStatus(user);
    }, 5000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkApprovalStatus(user);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, isApproved, checkApprovalStatus]);

  const handleShareLink = async () => {
    const url = new URL(window.location.href);
    url.search = '';
    if (query.trim()) url.searchParams.set('q', query.trim());
    const shareUrl = url.toString();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // clipboard API unavailable — try execCommand fallback
      const el = document.createElement('textarea');
      el.value = shareUrl;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  const handleExportPdf = async () => {
    if (!reportRef.current || !state.data) return;
    setPdfExporting(true);
    try {
      const blob = await exportVariantReportAsPdf(reportRef.current, state.data);
      const filename = variantPdfFilename(state.data.gene, state.data.proteinChange ?? query);
      if (navigator.share && navigator.canShare?.({ files: [new File([blob], filename, { type: 'application/pdf' })] })) {
        await navigator.share({ title: `VarCard: ${state.data.gene} ${state.data.proteinChange ?? query}`, files: [new File([blob], filename, { type: 'application/pdf' })] });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      }
    } finally {
      setPdfExporting(false);
    }
  };

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!isSignedIn || isApproved !== true) {
      openAuthScreen('sign-in');
      return;
    }
    const variantQueries = parseVariantQueries(query);
    if (variantQueries.length === 0) return;

    setBatchResults([]);
    setActiveBatchTab('summary');
    setState({ status: 'loading', data: null, error: null });

    const hasClinicalContext = Object.values(clinicalContext).some(value => value !== null && value !== '');
    const contextForAnalysis = hasClinicalContext ? clinicalContext : null;

    if (variantQueries.length === 1) {
      try {
        const result = await analyzeVariant(variantQueries[0], contextForAnalysis);
        setState({ status: 'success', data: result, error: null });
      } catch (error) {
        setState({
          status: 'error',
          data: null,
          error: error instanceof Error ? error.message : 'An unexpected error occurred',
        });
      }
      return;
    }

    const results = await runWithConcurrency(variantQueries, 3, async (variantQuery): Promise<BatchAnalysisResult> => {
      try {
        const result = await analyzeVariant(variantQuery, contextForAnalysis);
        return { query: variantQuery, status: 'success', data: result, error: null };
      } catch (error) {
        return {
          query: variantQuery,
          status: 'error',
          data: null,
          error: error instanceof Error ? error.message : 'An unexpected error occurred',
        };
      }
    });

    setBatchResults(results);
    const firstSuccessfulResult = results.find(result => result.status === 'success')?.data ?? null;
    setState({
      status: firstSuccessfulResult ? 'success' : 'error',
      data: firstSuccessfulResult,
      error: firstSuccessfulResult ? null : 'All variant analyses failed.',
    });
  };

  const handleGoogleLogin = async () => {
    setApprovalError(null);
    setShowAuthScreen(true);

    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(AUTH_SCREEN_STORAGE_KEY, '1');
    }

    try {
      // Only use redirect for in-app browsers where popups are blocked (Telegram, FB, Instagram, Line).
      // Safari and standard browsers must use popup to avoid ITP sessionStorage partitioning.
      const preferRedirect = typeof navigator !== 'undefined' && /Telegram|FBAN|FBAV|Instagram|Line\//i.test(navigator.userAgent);
      await signInWithGoogle({ preferRedirect });
    } catch (error) {
      setApprovalError(normalizeAuthErrorMessage(error));
      setShowAuthScreen(true);
    }
  };

  const handleEmailAuth = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setApprovalError('Enter both email and password.');
      return;
    }

    try {
      setAuthSubmitting(true);
      setApprovalError(null);
      if (authMode === 'sign-up') {
        await signUpWithEmailPassword(trimmedEmail, password);
      } else {
        await signInWithEmailPassword(trimmedEmail, password);
      }
    } catch (error) {
      setApprovalError(normalizeAuthErrorMessage(error));
      setShowAuthScreen(true);
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await signOutFromFirebase();
  };

  const openAuthScreen = (mode: 'sign-in' | 'sign-up' = 'sign-in') => {
    setApprovalError(null);
    setAuthMode(mode);
    setShowAuthScreen(true);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(AUTH_SCREEN_STORAGE_KEY, '1');
    }
  };

  const closeAuthScreen = () => {
    setApprovalError(null);
    setShowAuthScreen(false);
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(AUTH_SCREEN_STORAGE_KEY);
    }
  };

  const handleNewSearch = () => {
    setQuery('');
    setClinicalContext({ ...EMPTY_CLINICAL_CONTEXT });
    setState({ status: 'idle', data: null, error: null });
    setBatchResults([]);
    setActiveBatchTab('summary');
  };

  const GoogleIcon = () => (
    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1 .67-2.28 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 12-4.53z" fill="#EA4335" />
    </svg>
  );

  const examples = ['PKP2 R79X', 'KCNH2 A561V', 'MYH7 R403Q', 'LMNA R190W'];

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-brand-600 font-bold text-2xl">α</div>
      </div>
    );
  }

  const isSignedIn = !!user;
  const isLikelyInAppBrowser = typeof navigator !== 'undefined' && /Telegram|FBAN|FBAV|Instagram|Line\//i.test(navigator.userAgent);

  const renderAuthScreen = (allowBack: boolean) => (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-[32px] border border-slate-200/60 bg-white shadow-xl">
        <div className="px-8 py-10 text-center sm:px-10 sm:py-12">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-700">
            Restricted Access
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Welcome to VARCARD.IO</h1>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-500">
            Sign in with your approved clinical account to access AlphaVar Explorer.
          </p>

          {isLikelyInAppBrowser && (
            <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-800">
              Telegram&apos;s in-app browser can break Google sign-in. If Google redirect fails, use email/password below or open this page in Safari/Chrome.
            </div>
          )}

          {approvalError && (
            <div className="mt-5 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-left text-xs text-rose-600">
              <strong>Auth Error:</strong> {approvalError}
            </div>
          )}

          {allowBack && (
            <div className="mt-6 flex justify-start">
              <button
                type="button"
                onClick={closeAuthScreen}
                className="text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                ← Back
              </button>
            </div>
          )}

          <div className="mt-4 flex rounded-2xl bg-slate-100 p-1 text-sm font-medium text-slate-600">
            <button
              type="button"
              onClick={() => setAuthMode('sign-in')}
              className={`flex-1 rounded-[14px] px-4 py-2.5 transition-colors ${authMode === 'sign-in' ? 'bg-white text-slate-900 shadow-sm' : 'hover:text-slate-900'}`}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('sign-up')}
              className={`flex-1 rounded-[14px] px-4 py-2.5 transition-colors ${authMode === 'sign-up' ? 'bg-white text-slate-900 shadow-sm' : 'hover:text-slate-900'}`}
            >
              Sign up
            </button>
          </div>

          <div className="mt-5 space-y-3 text-left">
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Email</label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-brand-300 focus:ring-4 focus:ring-brand-100"
                placeholder="you@clinic.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Password</label>
              <input
                type="password"
                autoComplete={authMode === 'sign-up' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void handleEmailAuth();
                  }
                }}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-brand-300 focus:ring-4 focus:ring-brand-100"
                placeholder={authMode === 'sign-up' ? 'Create a password' : 'Enter your password'}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => void handleEmailAuth()}
            disabled={authSubmitting}
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-[20px] bg-slate-950 px-8 py-5 text-base font-semibold text-white shadow-lg shadow-slate-200 transition-all hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {authSubmitting
              ? authMode === 'sign-up'
                ? 'Creating account...'
                : 'Signing in...'
              : authMode === 'sign-up'
                ? 'Create account'
                : 'Sign in with email'}
          </button>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            onClick={() => void handleGoogleLogin()}
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-[20px] border border-slate-200 bg-white px-8 py-5 text-base font-semibold text-slate-900 shadow-sm transition-all hover:bg-slate-50 active:scale-[0.98]"
          >
            <GoogleIcon />
            Sign in with Google
          </button>

        </div>
      </div>
    </div>
  );

  if (!isSignedIn && showAuthScreen) {
    return renderAuthScreen(true);
  }

  if (isSignedIn && (isLoadingAuth || isCheckingApproval || isApproved === null)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse text-brand-600 font-bold text-2xl mb-3">α</div>
          <p className="text-sm text-slate-500">Verifying access…</p>
        </div>
      </div>
    );
  }

  if (isSignedIn && isApproved === false) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">
            Awaiting Approval
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Access Requested</h1>
          <p className="text-slate-600 leading-relaxed">
            <strong>{user?.email}</strong> is pending review.
          </p>
          {approvalError && (
            <div className="mt-4 rounded-xl bg-rose-50 border border-rose-100 p-3 text-xs text-rose-700 text-left">
              {approvalError}
            </div>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => void checkApprovalStatus(user)}
              className="px-5 py-3 rounded-xl bg-brand-600 text-white font-semibold hover:bg-brand-700 transition-colors"
            >
              Refresh Status
            </button>
            <button
              onClick={handleLogout}
              className="px-5 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-20">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button 
            onClick={handleNewSearch}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
              α
            </div>
            <span className="font-bold text-lg md:text-xl tracking-tight leading-tight text-left">
              <span style={{ background: 'linear-gradient(to right, #10b981, #0d9488)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                VARCARD.IO
              </span>
              <span className="text-slate-800 ml-1 hidden sm:inline">
                AlphaVar Explorer
              </span>
            </span>
          </button>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setShowAbout(true)}
              className="px-3 py-1.5 md:px-4 md:py-2 text-sm font-medium text-slate-600 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
            >
              About
            </button>
            {!isSignedIn ? (
              <button
                onClick={() => openAuthScreen('sign-in')}
                className="flex items-center bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg font-bold hover:bg-slate-50 transition-colors shadow-sm cursor-pointer text-sm"
              >
                <GoogleIcon />
                Sign In
              </button>
            ) : (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg font-medium hover:bg-slate-50 transition-colors shadow-sm cursor-pointer text-xs"
                title="Sign Out"
              >
                <GoogleIcon />
                <span className="max-w-[120px] truncate hidden sm:inline">{user?.email}</span>
                <span className="ml-1 border-l border-slate-200 pl-2">Sign Out</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* About Modal */}
      {showAbout && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAbout(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-brand-600 to-indigo-600 px-6 py-8 sm:px-8 sm:py-12 text-white">
              <button
                onClick={() => setShowAbout(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white/20 border-4 border-white/40 overflow-hidden flex-shrink-0 shadow-lg">
                  <img
                    src="/lior-jankelson.jpg"
                    alt="Lior Jankelson, MD, PhD"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="text-center sm:text-left">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-1">Lior Jankelson, MD, PhD</h2>
                  <p className="text-white/80 text-lg">Physician-Scientist | Cardiac Electrophysiology</p>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-6 sm:px-8 sm:py-8">
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  About
                </h3>
                <div className="mb-4 pb-4 border-b border-slate-200">
                  <h4 className="text-base font-bold text-slate-900 mb-2">
                    Lior Jankelson MD, PhD
                  </h4>
                  <p className="text-sm text-slate-600 mb-1">
                    Associate Professor of Medicine, Electrophysiology, NYU Grossman School of Medicine
                  </p>
                  <p className="text-sm text-slate-600">
                    Associate Professor of Biomedical Engineering, NYU Tandon School of Engineering
                  </p>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Dr. Lior Jankelson is a physician-scientist with expertise in cardiac electrophysiology and computational cardiology.
                  Dr. Jankelson combines his clinical practice with cutting-edge biomedical engineering research, developing computational tools that translate
                  complex clinically relevant problems into actionable insights. His work focuses on leveraging artificial intelligence
                  and machine learning to provide improved, precision-directed systems for clinical care. AlphaVar Explorer provides advanced
                  pathogenicity prediction tools accessible to clinicians and researchers, combining state-of-the-art AI models like
                  AlphaMissense and AlphaGenome with comprehensive clinical databases.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  Connect
                </h3>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://scholar.google.com/citations?user=CddM4EUAAAAJ&hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#4285F4] text-white rounded-lg text-sm font-medium hover:bg-[#3367D6] transition-colors"
                  >
                    Google Scholar
                  </a>
                  <a
                    href="https://x.com/ExtraStim"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2] text-white rounded-lg text-sm font-medium hover:bg-[#1a8cd8] transition-colors"
                  >
                    X (Twitter)
                  </a>
                  <a
                    href="https://www.linkedin.com/in/lior-jankelson/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white rounded-lg text-sm font-medium hover:bg-[#006399] transition-colors"
                  >
                    LinkedIn
                  </a>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  About AlphaVar Explorer
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm">
                  AlphaVar Explorer integrates multiple state-of-the-art tools for genomic variant interpretation,
                  including Google DeepMind's AlphaMissense for pathogenicity prediction, AlphaGenome for
                  regulatory impact analysis, and comprehensive clinical databases like ClinVar, COSMIC, and gnomAD.
                  The platform uses Generative AI to provide natural language interactions and clinical interpretations.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 sm:px-8 bg-slate-50 border-t border-slate-200">
              <button
                onClick={() => setShowAbout(false)}
                className="w-full py-3 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-xl transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-16">
        {/* Search Section */}
        <div className={`transition-all duration-500 ease-in-out ${
          state.status !== 'idle' ? 'mb-10' : 'min-h-[40vh] md:min-h-[50vh] flex flex-col justify-center items-center mb-0'
        }`}>
          <div className={`w-full max-w-2xl ${state.status === 'idle' ? 'text-center' : ''}`}>
            {state.status === 'idle' && (
              <div className="mb-8 animate-fade-in">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 md:mb-6 tracking-tight">
                  Search <span className="text-brand-600">AlphaVar</span>
                </h1>
                <p className="text-base md:text-lg text-slate-600 mb-6 md:mb-8 max-w-xl mx-auto">
                  Enter a variant to begin deep genetic analysis.
                </p>
              </div>
            )}

            {/* Search Form */}
            <div className="space-y-3">
              <form onSubmit={handleSearch} className="relative group z-10">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <div className="absolute top-4 md:top-5 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className={`h-6 w-6 ${state.status === 'loading' ? 'text-brand-500 animate-pulse' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <textarea
                      ref={queryInputRef}
                      value={query}
                      onChange={handleQueryChange}
                      placeholder={"Enter one variant, or paste multiple variants on separate lines..."}
                      rows={2}
                      className="block w-full resize-none overflow-hidden pl-12 pr-4 py-4 md:py-5 border-2 border-slate-200 rounded-2xl leading-6 bg-white placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 transition-all text-base md:text-lg shadow-sm"
                      disabled={state.status === 'loading'}
                    />
                  </div>
                  {!isSignedIn ? (
                    <button
                      type="button"
                      onClick={() => openAuthScreen('sign-in')}
                      className="w-full sm:w-auto px-8 py-4 md:py-5 bg-white border-2 border-slate-200 text-slate-700 font-bold text-lg rounded-2xl transition-all flex items-center justify-center hover:shadow-xl hover:scale-105 transform cursor-pointer"
                    >
                      <GoogleIcon />
                      Sign In
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className={`w-full sm:w-auto px-10 py-4 md:py-5 text-white font-bold text-lg rounded-2xl transition-all flex items-center justify-center disabled:opacity-90 shadow-lg ${
                        state.status === 'loading'
                          ? 'bg-slate-800 scale-95 shadow-inner'
                          : 'hover:shadow-xl hover:scale-105 transform'
                      }`}
                      style={state.status === 'loading' ? {} : {
                        background: 'linear-gradient(to right, #10b981, #0d9488)',
                        boxShadow: '0 0 0 4px rgba(16,185,129,0.3), 0 10px 15px -3px rgba(0,0,0,0.1)'
                      }}
                      disabled={state.status === 'loading' || !query.trim()}
                    >
                      {state.status === 'loading' ? (
                        <span className="text-white/80">Processing...</span>
                      ) : (
                        <>
                          Go
                          <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>

              {parseVariantQueries(query).length > 1 && state.status !== 'loading' && (
                <div className="text-left text-xs text-slate-500">
                  Batch mode: {parseVariantQueries(query).length} variants will be analyzed in parallel, up to 3 at a time.
                </div>
              )}

              {/* New Search Button */}
              {(state.status === 'success' || state.status === 'error') && (
                <div className="flex justify-start">
                  <button
                    onClick={handleNewSearch}
                    className="w-full sm:w-auto px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors flex items-center justify-center sm:justify-start gap-2 border border-slate-200"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Search
                  </button>
                </div>
              )}

              {/* Clinical Context Input */}
              {state.status === 'idle' && (
                <div className="flex justify-center">
                  <ClinicalContextInput
                    clinicalContext={clinicalContext}
                    onChange={setClinicalContext}
                    disabled={state.status !== 'idle'}
                  />
                </div>
              )}
            </div>

            {state.status === 'idle' && (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2 animate-fade-in">
                <span className="text-sm text-slate-500 mr-2 w-full sm:w-auto text-center sm:text-left block sm:inline">Try:</span>
                {examples.map((ex) => (
                  <button
                    key={ex}
                    onClick={() => { setQuery(ex); }}
                    className="px-3 py-1 text-sm bg-white border border-slate-200 rounded-full text-slate-600 hover:border-brand-300 hover:text-brand-600 transition-colors shadow-sm"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Results Area */}
        <div className="mt-8 mb-20">
            {state.status === 'loading' && (
              <div className="w-full max-w-5xl mx-auto mt-12 text-center animate-fade-in px-4">
                <div className="inline-flex items-center justify-center w-24 h-24 mb-6 relative">
                  <div className="absolute inset-0 border-4 border-emerald-100/50 border-t-emerald-500 rounded-full animate-spin"></div>
                  <div 
                    className="absolute inset-0 border-4 border-teal-100/50 border-b-teal-500 rounded-full animate-spin" 
                    style={{ animationDirection: 'reverse', animationDuration: '1.5s', margin: '12px' }}
                  ></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-slate-700 pb-1">α</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 animate-pulse">Analyzing Genome...</h3>
                <p className="text-slate-500 mt-2">{parseVariantQueries(query).length > 1 ? `Analyzing ${parseVariantQueries(query).length} variants in parallel` : 'Fetching AlphaVar Genetic Intelligence'}</p>
              </div>
            )}

            {state.status === 'error' && (
              <div className="w-full max-w-2xl mx-auto mt-8 p-6 bg-red-50 border border-red-100 rounded-xl flex items-start gap-4 animate-fade-in">
                <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="font-semibold text-red-800">Analysis Failed</h3>
                  <p className="text-red-600 mt-1">{state.error}</p>
                  <button
                    onClick={handleNewSearch}
                    className="mt-3 text-sm text-red-700 underline hover:no-underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {state.status === 'success' && batchResults.length > 0 && (
              <div className="space-y-6 animate-fade-in">
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      <button
                        type="button"
                        onClick={() => setActiveBatchTab('summary')}
                        className={`whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${activeBatchTab === 'summary' ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:text-slate-900'}`}
                      >
                        Summary
                      </button>
                      {batchResults.map((result, index) => (
                        <button
                          type="button"
                          key={`${result.query}-tab-${index}`}
                          onClick={() => setActiveBatchTab(index)}
                          className={`whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${activeBatchTab === index ? 'bg-brand-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:text-slate-900'}`}
                        >
                          {result.data?.gene || result.query}
                          <span className={`ml-2 inline-block h-2 w-2 rounded-full ${result.status === 'success' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="p-5">
                    {activeBatchTab === 'summary' ? (
                      <div className="space-y-5">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">Batch analysis summary</h3>
                          <p className="mt-1 text-sm text-slate-500">
                            {batchResults.filter(result => result.status === 'success').length} succeeded, {batchResults.filter(result => result.status === 'error').length} failed. Select a variant tab to review its full interpretation.
                          </p>
                        </div>
                        <div className="overflow-hidden rounded-xl border border-slate-200">
                          <div className="grid grid-cols-[1fr_0.8fr_0.8fr] gap-3 bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                            <span>Variant</span>
                            <span>Gene</span>
                            <span>Status</span>
                          </div>
                          {batchResults.map((result, index) => (
                            <button
                              type="button"
                              key={`${result.query}-summary-${index}`}
                              onClick={() => setActiveBatchTab(index)}
                              className="grid w-full grid-cols-[1fr_0.8fr_0.8fr] gap-3 border-t border-slate-100 px-3 py-3 text-left text-sm hover:bg-slate-50"
                            >
                              <span className="font-medium text-slate-900">{result.query}</span>
                              <span className="text-slate-600">{result.data?.gene || '—'}</span>
                              <span className={result.status === 'success' ? 'text-emerald-700' : 'text-rose-700'}>{result.status === 'success' ? 'Analyzed' : 'Failed'}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (() => {
                      const result = batchResults[activeBatchTab];
                      if (!result) return null;
                      return (
                        <div className="space-y-5">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <h3 className="text-lg font-semibold text-slate-900">{result.query}</h3>
                            <span className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${result.status === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>
                              {result.status === 'success' ? 'Analyzed' : 'Failed'}
                            </span>
                          </div>
                          {result.status === 'error' ? (
                            <p className="text-sm text-rose-600">{result.error}</p>
                          ) : result.data ? (
                            <div className="space-y-5">
                              <PathogenicityBar data={result.data} />
                              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                                <h4 className="text-sm font-semibold text-blue-900">Clinical Interpretation</h4>
                                <p className="mt-2 text-sm leading-relaxed text-blue-900">{result.data.description}</p>
                                {result.data.geneDiseaseCorrelation && (
                                  <p className="mt-3 text-sm leading-relaxed text-blue-900">
                                    <strong>Gene–disease:</strong> {result.data.geneDiseaseCorrelation.summary}
                                  </p>
                                )}
                              </div>
                              <ReportedClinicalFindingsCard data={result.data} />
                              <VariantReport data={result.data} />
                            </div>
                          ) : null}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {state.status === 'success' && batchResults.length === 0 && resultData(state.data) && (
              <div className="space-y-6" ref={reportRef}>
                <PathogenicityBar data={state.data!} />
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                      <span>📋</span>
                      Clinical Interpretation
                    </h3>
                    <button
                      type="button"
                      onClick={() => void handleExportPdf()}
                      disabled={pdfExporting}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors disabled:opacity-50"
                      title="Share as PDF"
                    >
                      {pdfExporting ? (
                        <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg><span className="text-xs">Generating…</span></>
                      ) : (
                        <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg><span className="text-xs">Share</span></>
                      )}
                    </button>                  </div>
                  <ReclassificationBadge info={state.data!.reclassificationInfo} />
                  <p className="text-slate-600 leading-relaxed text-[15px] mt-3">
                    {state.data!.description}
                  </p>
                  {state.data!.geneDiseaseCorrelation && (
                    <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
                      <h4 className="text-sm font-semibold text-blue-900 mb-2">Gene–Disease Correlation & Inheritance</h4>
                      <p className="text-sm text-blue-900 leading-relaxed">{state.data!.geneDiseaseCorrelation.summary}</p>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-blue-900 px-2.5 py-1 text-white">
                          Overall inheritance: {state.data!.geneDiseaseCorrelation.inheritance.join(', ')}
                        </span>
                        {state.data!.geneDiseaseCorrelation.evidenceLevel && (
                          <span className="rounded-full bg-white px-2.5 py-1 text-blue-800 border border-blue-100">
                            Overall evidence: {state.data!.geneDiseaseCorrelation.evidenceLevel}
                          </span>
                        )}
                      </div>
                      {state.data!.geneDiseaseCorrelation.phenotypeCorrelations?.length > 0 ? (
                        <div className="mt-4 overflow-hidden rounded-xl border border-blue-100 bg-white">
                          <div className="grid grid-cols-[1.2fr_0.8fr_0.7fr] gap-3 bg-blue-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-blue-900">
                            <span>Phenotype / disease</span>
                            <span>Inheritance</span>
                            <span>Evidence</span>
                          </div>
                          {state.data!.geneDiseaseCorrelation.phenotypeCorrelations.map((correlation) => (
                            <div key={`${correlation.phenotype}-${correlation.disease}`} className="grid grid-cols-[1.2fr_0.8fr_0.7fr] gap-3 border-t border-blue-50 px-3 py-3 text-sm text-slate-700">
                              <div>
                                <div className="font-medium text-slate-900">{correlation.phenotype}</div>
                                <div className="text-xs text-slate-500">{correlation.disease}</div>
                                {correlation.summary && <div className="mt-1 text-xs text-slate-600">{correlation.summary}</div>}
                              </div>
                              <div>{correlation.inheritance.join(', ')}</div>
                              <div>
                                <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-800 border border-blue-100">
                                  {correlation.evidenceLevel}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2 text-xs">
                          {state.data!.geneDiseaseCorrelation.diseases.map((disease) => (
                            <span key={disease} className="rounded-full bg-white px-2.5 py-1 text-blue-800 border border-blue-100">{disease}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <ClinicalFitCard
                  fit={state.data!.phenotypeGenotypeFit}
                  clinicalContext={state.data!.clinicalContext}
                />
                <ReportedClinicalFindingsCard data={state.data!} />
                <VariantChat variantData={state.data!} />
                <NearbyVariantsCard
                  variants={state.data!.nearbyPathogenicVariants}
                  queryPosition={(() => {
                    const match = state.data!.proteinChange?.match(/\d+/);
                    return match ? parseInt(match[0], 10) : null;
                  })()}
                  queryGene={state.data!.gene}
                />
                <ClinicalActionCard plan={state.data!.clinicalActionPlan} />
                <VariantReport data={state.data!} />
              </div>
            )}
        </div>

        {/* Disclaimer */}
        <div className="mt-12 mb-8 max-w-3xl mx-auto">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h4 className="text-sm font-semibold text-amber-800 mb-1">Research Use Only</h4>
                <p className="text-sm text-amber-700 leading-relaxed">
                  This website is intended for research and educational purposes only. The information provided should not be used
                  for clinical decision-making or as a substitute for professional medical advice, diagnosis, or treatment.
                  Always consult with qualified healthcare professionals and consider genetic counseling for clinical variant interpretation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 py-3 px-4 z-40">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between text-xs sm:text-sm text-slate-500 gap-2 sm:gap-0">
          <span>© 2026 VARCARD.IO AlphaVar Explorer</span>
          <button
            onClick={() => setShowAbout(true)}
            className="hover:text-brand-600 transition-colors"
          >
            Developed by Lior Jankelson, MD, PhD
          </button>
        </div>
      </footer>
    </div>
  );
};

// Helper to handle type safety
function resultData(data: any) {
    return data !== null;
}

const App: React.FC = () => {
  return (
    <AlphaVarExplorer />
  );
};

export default App;
